import React from 'react';
import { Collapse, Tag, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import resetIcon from '../../../img/reset.svg';
import TutorialButton from '@client/components/ui/TutorialButton';

/**
 * Reusable main filter panels component
 * @param {Object} props
 * @param {React.Component} [props.BasicFiltersComponent] - Basic filters component (optional)
 * @param {React.Component} [props.ExtraFiltersComponent] - Extra filters component (optional)
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFilterChange - Filter change handler
 * @param {number} [props.basicCount=0] - Count of basic filter selections
 * @param {number} [props.extraCount=0] - Count of extra filter selections
 * @param {Array<string>} [props.defaultActiveKeys] - Default active panel keys
 * @param {React.Component} [props.expandIcon] - Expand icon component (optional) - receives { isActive } prop
 * @param {Object} [props.icons] - Icon components for panels (optional)
 * @param {string} [props.searchByLabel] - Translation key for "Search by" label
 * @param {string} [props.extraFiltersLabel] - Translation key for "Extra filters" label
 * @param {Object} [props.aggregations] - Aggregations data (optional)
 * @param {Object} [props.filterLabels] - Labels for cold start restoration (optional)
 * @returns {JSX.Element}
 */
const MainFilterPanels = ({
  BasicFiltersComponent,
  ExtraFiltersComponent,
  filters = {},
  onFilterChange = () => {},
  basicCount = 0,
  extraCount = 0,
  defaultActiveKeys = ['1'],
  expandIcon,
  icons = {},
  searchByLabel = 'common|search_by',
  extraFiltersLabel = 'common|extra_filters',
  aggregations,
  filterLabels,
  onReset,
  searchData,
  searchMode,
  basicTutorialSlotKey,
  extraTutorialSlotKey,
}) => {
  const { t } = useTranslation();

  const filterPanels = [];

  if (BasicFiltersComponent) {
    filterPanels.push({
      key: '1',
      className: 'custom-collapse-panel',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {icons.search && <img src={icons.search} alt="Search" />}
          <p className="collapse-title">{t(searchByLabel)}</p>
        </div>
      ),
      extra: (
        <div
          className="flex items-center gap-2 ml-3"
          onClick={(e) => e.stopPropagation()}
        >
          {basicCount > 0 && (
            <Tag>
              {basicCount} {t('common|selection', { count: basicCount })}
            </Tag>
          )}
          {basicTutorialSlotKey && (
            <TutorialButton slotKey={basicTutorialSlotKey} />
          )}
        </div>
      ),
      children: (
        <BasicFiltersComponent
          filters={filters}
          onFilterChange={onFilterChange}
          aggregations={aggregations}
          filterLabels={filterLabels}
        />
      ),
    });
  }

  if (ExtraFiltersComponent) {
    filterPanels.push({
      key: '2',
      className: 'custom-collapse-panel extra-filter',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {icons.addFilters && <img src={icons.addFilters} alt="Add filters" />}
          <p className="collapse-title">{t(extraFiltersLabel)}</p>
          {extraCount > 0 && (
            <Tag>
              {extraCount} {t('common|selection', { count: extraCount })}
            </Tag>
          )}
        </div>
      ),
      extra: (
        <div
          className="flex items-center gap-2 ml-3"
          onClick={(e) => e.stopPropagation()}
        >
          {extraTutorialSlotKey && (
            <TutorialButton slotKey={extraTutorialSlotKey} />
          )}
          {onReset && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onReset();
              }}
              className="reset-button-with-icon"
            >
              <img src={resetIcon} alt="Reset" className="w-4 h-4 reset-icon" />
              {t('treatment-search|actions.reset', 'Réinitialiser')}
            </Button>
          )}
        </div>
      ),
      children: (
        <ExtraFiltersComponent
          filters={filters}
          onFilterChange={onFilterChange}
          aggregations={aggregations}
          filterLabels={filterLabels}
          searchData={searchData}
          searchMode={searchMode}
        />
      ),
    });
  }

  return (
    <Collapse
      defaultActiveKey={defaultActiveKeys}
      items={filterPanels}
      ghost
      expandIconPosition="start"
      expandIcon={expandIcon}
      accordion
    />
  );
};

export default React.memo(MainFilterPanels);